import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import IconComponent from "../../cubeComponent/icons";
import {
  getProjectContractPageUrl,
  getProjectDashboardPageUrl,
  getProjectEstimatesPageUrl,
  getProjectFilesPageUrl,
  getProjectGeneralClashPageUrl,
  getProjectIssuesPageUrl,
  getProjectMailboxPageUrl,
  getProjectReviewsPageUrl,
  getProjectSchedulesPageUrl,
  getProjectTasksPageUrl,
  getProjectTransmittalsPageUrl,
  getProjectSettingsPageUrl,
  getProjectWorkspacesListPageUrl,
  getProjectFormsPageUrl,
  getProjectRiskManagementListPageUrl,
} from "../../services/utils/helpers";
import css from "./index.module.css";
import useStickySidebar from "./useStickySidebar";
import { getProjectObject } from "@Actions/projectSetting";
import { Chip, Divider, Drawer, List, ListItem, ListItemIcon } from "@mui/material";

function Sidebar({ userData }) {
  const { layout, currentSelectedSidebarCell: activeCell } = useSelector((state) => state);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId");
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showScrollUp, setShowScrollUp] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(true);

  const midIconsDivRef = useRef();

  const { sideBarMidIcons, sideBarUpperFixedIcons, sideBarLowerFixedIcons } = useStickySidebar({
    layout,
    projectId,
    path: {
      Contract: (projectId) => getProjectContractPageUrl(projectId),
      Dashboard: (projectId) => getProjectDashboardPageUrl(projectId),
      Estimates: (projectId) => getProjectEstimatesPageUrl(projectId),
      Files: (projectId) => getProjectFilesPageUrl(projectId),
      GeneralClash: (projectId) => getProjectGeneralClashPageUrl(projectId),
      Issues: (projectId) => getProjectIssuesPageUrl(projectId),
      Mailbox: (projectId) => getProjectMailboxPageUrl(projectId),
      Reviews: (projectId) => getProjectReviewsPageUrl(projectId),
      Schedules: (projectId) => getProjectSchedulesPageUrl(projectId),
      Tasks: (projectId) => getProjectTasksPageUrl(projectId),
      Transmittals: (projectId) => getProjectTransmittalsPageUrl(projectId),
      Settings: (projectId) => getProjectSettingsPageUrl({ projectId, tab: "general_information" }),
      WorkspacesList: (projectId) => getProjectWorkspacesListPageUrl(projectId),
      RiskManagement: (projectId) => getProjectRiskManagementListPageUrl(projectId),
      Forms: (projectId, moduleName) => getProjectFormsPageUrl(projectId, moduleName),
    },
  });

  useEffect(() => {
    const sideBarState = localStorage.getItem("isSideBarExpanded");
    setIsExpanded(sideBarState);
  }, [sideBarMidIcons]);

  const handleScroll = () => {
    const scrollableContent = midIconsDivRef.current;
    if (scrollableContent) {
      setShowScrollUp(scrollableContent.scrollTop > 0);
      setShowScrollDown(
        scrollableContent.scrollHeight - scrollableContent.scrollTop !== scrollableContent.clientHeight,
      );
    }
  };

  const handleUpArrowClick = () => {
    const scrollableContent = midIconsDivRef.current;
    if (scrollableContent) {
      scrollableContent.scrollBy({ top: -100, behavior: "smooth" });
    }
    handleScroll();
  };

  const handleDownArrowClick = (customHeight = 100) => {
    const scrollableContent = midIconsDivRef.current;
    if (scrollableContent) {
      scrollableContent.scrollBy({ top: customHeight, behavior: "smooth" });
    }
    handleScroll();
  };

  useEffect(() => {
    handleScroll();
  }, [sideBarMidIcons]);

  useEffect(() => {
    if (projectId) {
      dispatch(getProjectObject({ id: projectId }));
    }
  }, [projectId]);

  const [showSettings, setShowSettings] = useState(true);

  useEffect(() => {
    if (layout.PROJECT_SETTINGS?.ENABLE === false) {
      setShowSettings(false);
    } else if (
      typeof layout.PROJECT_SETTINGS?.ENABLE === "object" &&
      layout.PROJECT_SETTINGS?.ENABLE?.CONDITION?.COMPANY_NAME
    ) {
      setShowSettings(userData?.company?.name == layout.PROJECT_SETTINGS?.ENABLE?.CONDITION?.COMPANY_NAME);
    }
  }, [layout, userData]);

  const handleSideBarExpandClick = () => {
    if (isExpanded) {
      localStorage.setItem("isSideBarExpanded", "");
      setIsExpanded(false);
    } else {
      localStorage.setItem("isSideBarExpanded", "yes");
      setIsExpanded(true);
    }
  };

  const handleRowClick = (path) => {
    navigate(path);
  };

  const getBetaTag = () => (
    <Chip
      size="small"
      sx={{
        fontWeight: 400,
        background: "#666a84",
        color: "white",
      }}
      label="BETA"
    />
  );
  const getIconsList = ({ iconsList, type, ref = undefined, css }) => {
    return (
      <List
        ref={ref}
        sx={{
          paddingBottom: "10px",
          border: "none",
          display: "flex",
          paddingTop: 0,
          flexDirection: "column",
          gap: "0px",
          ...css,
          opacity: type == "settings" && !showSettings ? 0.5 : 1,
          pointerEvents: type == "settings" && !showSettings && "none",
        }}
      >
        {iconsList.map(
          ({ allow, toolTip, path, name, svg, id, showBetaTag }, index) =>
            allow && (
              <ListItem key={index} disablePadding data-testid={name + "_SidebarIcons"}>
                <div className="flex justify-center py-4 items-center ps-2">
                  {activeCell === id ? (
                    <div
                      style={{ borderStartEndRadius: "6px", borderEndEndRadius: "6px" }}
                      className={`w-2 h-3 bg-[linear-gradient(#6bbbe3,_#3e8ab0)] absolute start-0`}
                    ></div>
                  ) : null}
                  <ListItemIcon sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <IconComponent
                      position="right"
                      onClick={() => handleRowClick(path)}
                      toolTipContent={toolTip}
                      showGradient={activeCell === id}
                      name={svg}
                      menuShow={isExpanded}
                      parentContainerClass={`flex gap-5 ${isExpanded ? "ps-4" : ""}`}
                    >
                      {showBetaTag ? (
                        <div className="flex gap-3 items-center">
                          <span className="text-sm text-black">{name}</span>
                          {getBetaTag()}
                        </div>
                      ) : (
                        <div className="text-sm text-black">{name}</div>
                      )}
                    </IconComponent>
                  </ListItemIcon>
                </div>
              </ListItem>
            ),
        )}
      </List>
    );
  };

  function getDrawer() {
    return (
      <Drawer
        sx={{
          width: isExpanded ? "250px" : "72px",
          "& .MuiDrawer-paper": {
            width: isExpanded ? "250px" : "72px",
            zIndex: 9,
            position: "fixed",
            paddingTop: "100px",
            height: "100%",
            border: "none",
            display: "flex",
            overflow: "auto",
            backgroundColor: "#f0f5f9",
          },
        }}
        variant="permanent"
        open={isExpanded}
        anchor={"start"}
      >
        <div>
          {getIconsList({ iconsList: sideBarUpperFixedIcons, height: "450px", css: {} })}
          <Divider />
        </div>
        <div className="flex overflow-auto flex-col h-full">
          <div
            onClick={handleUpArrowClick}
            id="upArrow"
            className={`flex cursor-pointer bg-surface_neutral p-3 justify-center items-center`}
          >
            {showScrollUp && <IconComponent name="BoldUpArrow" />}
          </div>
          {getIconsList({
            iconsList: sideBarMidIcons,
            type: "midIcons",
            ref: midIconsDivRef,
            css: { overflow: "hidden", height: "100%" },
          })}
          <div
            onClick={() => handleDownArrowClick()}
            className={`cursor-pointer flex bg-surface_neutral justify-center items-center p-3`}
          >
            {showScrollDown && <IconComponent name="BoldDownArrow" />}
          </div>
        </div>
        <div>
          <Divider />
          {getIconsList({ iconsList: sideBarLowerFixedIcons, type: "settings", css: { paddingTop: "14px" } })}
        </div>
      </Drawer>
    );
  }

  return (
    <div className={`${css.sidebar} ${localStorage.getItem("isSideBarExpanded") ? css.expanded : css.collapsed}`}>
      {getDrawer()}
      <div
        onClick={handleSideBarExpandClick}
        className={isExpanded ? `${css.midHorizontalLineCollapsed}` : `${css.midHorizontalLineExpanded}`}
      />
    </div>
  );
}

export default Sidebar;
